import store from '@/store/store';
import i18n from '@/i18n';

export const HOUR_FORMATS = {
    HOURS_MINUTES: 'HOURS_MINUTES',
    DECIMAL: 'DECIMAL'
};

export const DEFAULT_HOUR_FORMAT = HOUR_FORMATS.HOURS_MINUTES;

const NUMBER_LOCALES = {pt: 'pt-BR', en: 'en-US', es: 'es-ES'};

function numberLocale() {
    return NUMBER_LOCALES[i18n.locale] || 'pt-BR';
}

function toNumber(value) {
    if (typeof value === 'string' && value.trim() !== '' && !isNaN(value)) {
        return Number(value);
    }

    if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
        return null;
    }

    return value;
}

export function getHourFormat() {
    return store.getters.getHourFormat || DEFAULT_HOUR_FORMAT;
}

export function isDecimalHourFormat() {
    return getHourFormat() === HOUR_FORMATS.DECIMAL;
}

export function formatHours(hours, options = {}) {
    const {decimals = 2, unit = true, format = getHourFormat(), empty = '-'} = options;
    const value = toNumber(hours);

    if (value === null) {
        return empty;
    }

    if (format === HOUR_FORMATS.DECIMAL) {
        const formatted = new Intl.NumberFormat(numberLocale(), {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(value);
        return unit ? `${formatted} h` : formatted;
    }

    const sign = value < 0 ? '-' : '';
    const totalSeconds = Math.round(Math.abs(value) * 3600);
    const h = Math.floor(totalSeconds / 3600);
    const min = Math.floor((totalSeconds % 3600) / 60);
    const sec = totalSeconds % 60;
    const pad = n => String(n).padStart(2, '0');

    if (h > 0) {
        const formattedHours = new Intl.NumberFormat(numberLocale(), {maximumFractionDigits: 0}).format(h);
        return `${sign}${formattedHours}h${pad(min)}m${pad(sec)}s`;
    }

    if (min > 0) {
        return `${sign}${min}m${pad(sec)}s`;
    }

    return `${sign}${sec}s`;
}

export function toCsvDecimalHours(hours) {
    const value = toNumber(hours);
    if (value === null) {
        return '';
    }
    return parseFloat(value.toFixed(4)).toString().replace('.', ',');
}

export function secondsToCsvDecimalHours(seconds) {
    const value = toNumber(seconds);
    return toCsvDecimalHours(value === null ? null : value / 3600);
}

export function formatSecondsAsHours(seconds, options = {}) {
    const value = toNumber(seconds);
    return formatHours(value === null ? null : value / 3600, options);
}

export function formatMinutesAsHours(minutes, options = {}) {
    const value = toNumber(minutes);
    return formatHours(value === null ? null : value / 60, options);
}

export function formatMillisecondsAsHours(milliseconds, options = {}) {
    const value = toNumber(milliseconds);
    return formatHours(value === null ? null : value / 3600000, options);
}
